import { Badge, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import {
  OsLotReviewScopeParams,
  osLotReviewScopeSearch,
} from '../../lib/os-lot-review-scope';
import { OsLotReviewDetail } from '../../lib/types/os-lot-reviews';

import { getLotReviewMetrics, reviewStatusColor } from './os-lot-review-checks';
import {
  OsLotReviewIssueList,
  OsLotReviewMetricPanel,
} from './os-lot-review-metric';
import { OsLotReviewTelepacSource } from './os-lot-review-telepac';

import { usePermissions } from '@auth/data-access';
import { TsrTextLink } from '@ui';

export function OsLotReviewDetailHeader({
  lot,
  scope,
}: {
  lot: OsLotReviewDetail | undefined;
  scope: OsLotReviewScopeParams;
}) {
  const { t } = useTranslation();
  const { isAdmin, isIndustrial } = usePermissions();
  const metrics = lot ? getLotReviewMetrics(lot) : null;
  const cropYear = lot
    ? [lot.species?.name, lot.year].filter(Boolean).join(' ')
    : null;
  const contractId = lot?.contract_id;

  return (
    <Paper p="md">
      <Stack gap="md">
        <div>
          <TsrTextLink
            search={osLotReviewScopeSearch(scope)}
            size="sm"
            td="underline"
            to="/os/lot-reviews"
          >
            {t('osLotReviews.back_to_list')}
          </TsrTextLink>
          <Group align="flex-start" justify="space-between" mt={4} wrap="wrap">
            <div>
              <Title order={2}>{lot?.farm_name ?? '—'}</Title>
              <Group gap="xs" mt={4} wrap="wrap">
                {cropYear ? (
                  <Text c="gray.7" size="sm">
                    {cropYear}
                  </Text>
                ) : null}
                {isAdmin && contractId != null ? (
                  <TsrTextLink
                    params={{ contractId: contractId.toString() }}
                    size="sm"
                    td="underline"
                    to="/os/contracts/$contractId"
                  >
                    {t('osLotReviews.open_contract')}
                  </TsrTextLink>
                ) : null}
              </Group>
            </div>
            {lot ? (
              <Group gap="xs">
                {lot.has_conditionally_sustainable_fields ? (
                  <Badge color="orange" variant="light">
                    {t('osLotReviews.conditionally_sustainable.yes', {
                      count: lot.conditionally_sustainable_fields_count,
                    })}
                  </Badge>
                ) : null}
                <Badge
                  color={reviewStatusColor(lot.review_status)}
                  variant="light"
                >
                  {t(`osLotReviews.review_status.${lot.review_status}`)}
                </Badge>
              </Group>
            ) : null}
          </Group>
        </div>

        {metrics ? <OsLotReviewIssueList metrics={metrics} /> : null}

        {metrics ? (
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <OsLotReviewMetricPanel
              label={t('osLotReviews.columns.tonnage')}
              metric={metrics.tonnage}
              referenceLabel={t('osLotReviews.metrics.oleoze')}
            />
            <OsLotReviewMetricPanel
              label={t('osLotReviews.columns.real_yield')}
              metric={metrics.yield}
              referenceLabel={t('osLotReviews.metrics.range')}
            />
            <OsLotReviewMetricPanel
              label={t('osLotReviews.columns.area')}
              metric={metrics.area}
              referenceLabel={t('osLotReviews.metrics.theoretical_total')}
            />
          </SimpleGrid>
        ) : null}

        {lot && !isIndustrial ? (
          <OsLotReviewTelepacSource provenance={lot.provenance} />
        ) : null}
      </Stack>
    </Paper>
  );
}
